import { create } from 'zustand'
import posthog from 'posthog-js'
import { auth, googleProvider, setAuthTenant } from '../config/firebase.config'
import { signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth'
import {
  getCachedTenantConfig,
  initializeTenant,
  extractSubdomain,
} from '../services/tenant.service'
import { trackEvent } from '../services/posthog'

/**
 * Get valid tenant config for the current subdomain.
 * If cached config is for a different subdomain, fetches fresh config.
 */
const getValidTenantConfig = async () => {
  const currentSubdomain = extractSubdomain()
  const cachedSubdomain = sessionStorage.getItem('currentSubdomain')

  // If subdomain matches, use cached config
  if (currentSubdomain === cachedSubdomain) {
    return getCachedTenantConfig()
  }

  // Subdomain changed or no cache - fetch fresh config
  if (currentSubdomain) {
    return initializeTenant()
  }

  return null
}

const MINIMUM_TENANT_ID_LENGTH = 5

const initialState = {
  user: null,
  idToken: null,
  loading: true,
  initialized: false,
  headersReady: false,
  tenantError: null,
  loginError: null,
}

const useAuthStore = create((set) => ({
  ...initialState,

  signIn: async (email, password) => {
    try {
      set({ loading: true, loginError: null })

      // Ensure we have valid tenant config for current subdomain
      const tenantConfig = await getValidTenantConfig()
      const staffTenantId = tenantConfig?.tenants?.staff
      if (staffTenantId && staffTenantId.length > MINIMUM_TENANT_ID_LENGTH) {
        setAuthTenant(staffTenantId)
      }

      const result = await signInWithEmailAndPassword(auth, email, password)
      set({ loading: false, loginError: null })
      trackEvent('staff.login_succeeded', {
        login_method: 'email',
        corporate_slug: extractSubdomain(),
      })
      return { success: true, user: result.user }
    } catch (error) {
      let errorMessage = 'Login failed'

      if (error.code) {
        errorMessage = error.code
      } else if (error.message) {
        errorMessage = error.message
      }

      set({ loading: false, loginError: errorMessage })

      return { success: false, error: errorMessage }
    }
  },

  clearLoginError: () => {
    set({ loginError: null })
  },

  signInWithGoogle: async () => {
    try {
      set({ loading: true })

      // Ensure we have valid tenant config for current subdomain
      const tenantConfig = await getValidTenantConfig()
      const staffTenantId = tenantConfig?.tenants?.staff
      if (staffTenantId && staffTenantId.length > MINIMUM_TENANT_ID_LENGTH) {
        setAuthTenant(staffTenantId)
      }

      const result = await signInWithPopup(auth, googleProvider)
      set({ loading: false })
      trackEvent('staff.login_succeeded', {
        login_method: 'google',
        corporate_slug: extractSubdomain(),
      })
      return { success: true, user: result.user }
    } catch (error) {
      set({ loading: false })

      let errorMessage = error.message || 'Google sign-in failed'

      if (error.code) {
        errorMessage = error.code
      }

      return { success: false, error: errorMessage }
    }
  },

  signOut: async () => {
    try {
      await auth.signOut()
      // Reset PostHog identity to prevent cross-user attribution
      // (common in studios where staff share front-desk computers)
      posthog.reset()
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },

  // Simple remember me support
  initializeRememberMe: () => {
    // Just return false for now - can be enhanced later
    return false
  },

  // Reset store to initial state
  reset: () => set(initialState),
}))

export default useAuthStore
